import type { ConsultTaskAssignee } from '../one-to-one/task-assignee.js';

export type ConsultType = '1:1' | 'group' | 'flexible' | 'ffs';

/**
 * Every declared `ConsultType`, as a runtime value.
 *
 * @remarks Typed `readonly ConsultType[]` rather than inferred from the literals, so adding a member
 * to the union without adding it here is a type error in {@link isConsultType}'s tests rather than a
 * silently narrower guard. This is the ONE place the union exists at runtime.
 */
const CONSULT_TYPES: readonly ConsultType[] = ['1:1', 'group', 'flexible', 'ffs'];

/**
 * Narrow a wire value to a declared `ConsultType`.
 *
 * @remarks `consultType` used to be assigned straight into state from the exchange response while
 * every immediate neighbour was validated — `consultStage` against both its literals, `member` and
 * `taskAssignee` field by field. It decides which tabs exist, which data hooks are enabled and which
 * hero copy renders, so an unexpected value is a wholesale change in what the member is offered,
 * presented confidently. An unrecognised value now resolves to `undefined`, which the branch
 * functions already handle as "both modes offered".
 */
export function isConsultType(value: unknown): value is ConsultType {
  return typeof value === 'string' && (CONSULT_TYPES as readonly string[]).includes(value);
}

export type ConsultStage = 'initial' | 'follow-up';

/** Member identity from the magic link, used to pre-fill (and lock) webinar registration. */
export type ConsultMemberDetails = {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
};

export type ExchangeResult = {
  jwt: string;
  expiresInSeconds: number;
  consultType?: ConsultType;
  /** Fee-for-service: which 1:1 subsections to show; omitted when absent on token. */
  consultStage?: ConsultStage;
  state?: string;
  /**
   * FHIR `Task` assignee: personalized Zoom Scheduler link; shown above the program follow-up
   * CTA when present.
   */
  taskAssignee?: ConsultTaskAssignee;
  /** When present, used to pre-fill webinar registration (from backend/member lookup). */
  member?: ConsultMemberDetails;
  /**
   * Whether the member must authorize a card for administrative fees (a missed or late-cancelled
   * FFS consult) before the 1:1 tab offers booking. Omitted or `false` ⇒ no gate.
   */
  adminFeeCardRequired?: boolean;
};
